import { useState } from 'react';
import { allDirectors, allGenres, allYears } from '@/lib/data';

export interface Filters {
  year: string;
  director: string;
  genre: string;
  rating: string;
  sort: string;
}

export const defaultFilters: Filters = {
  year: 'all',
  director: 'all',
  genre: 'all',
  rating: 'all',
  sort: 'default',
};

const ratingOptions = [
  { value: 'all', label: '不限评分' },
  { value: '8.5', label: '8.5 分以上' },
  { value: '8', label: '8 分以上' },
  { value: '7.5', label: '7.5 分以上' },
];

const sortOptions = [
  { value: 'default', label: '默认推荐' },
  { value: 'year', label: '按上映时间' },
  { value: 'rating', label: '按评分高低' },
];

interface FilterBarProps {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
  resultCount: number;
}

export default function FilterBar({ filters, onChange, onReset, resultCount }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount = [
    filters.year !== 'all',
    filters.director !== 'all',
    filters.genre !== 'all',
    filters.rating !== 'all',
  ].filter(Boolean).length;

  const selectClass =
    'h-10 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer';

  return (
    <div className="rounded-lg border border-background-200/80 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center gap-2 h-10 px-3 rounded-md border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <i className="ri-equalizer-line text-primary-500" aria-hidden="true" />
            筛选
            {activeCount > 0 && (
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 font-label text-[11px] font-bold text-primary-950">
                {activeCount}
              </span>
            )}
          </button>
          <p className="hidden lg:flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-grid-line text-primary-500" aria-hidden="true" />
            共
            <span className="font-label font-bold text-foreground-950">{resultCount}</span>
            部影片符合当前筛选
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="hidden sm:inline text-sm text-foreground-500 whitespace-nowrap">排序</span>
          <select
            value={filters.sort}
            onChange={(e) => onChange({ sort: e.target.value })}
            className="h-10 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer w-full sm:w-44"
            aria-label="排序方式"
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`${open ? 'block' : 'hidden'} lg:block border-t border-background-100 p-4`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="mb-1.5 block font-label text-xs font-semibold text-foreground-600">上映年份</label>
            <select
              value={filters.year}
              onChange={(e) => onChange({ year: e.target.value })}
              className={selectClass}
              aria-label="按年份筛选"
            >
              <option value="all">全部年份</option>
              {allYears.map((y) => (
                <option key={y} value={String(y)}>
                  {y} 年
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block font-label text-xs font-semibold text-foreground-600">导演</label>
            <select
              value={filters.director}
              onChange={(e) => onChange({ director: e.target.value })}
              className={selectClass}
              aria-label="按导演筛选"
            >
              <option value="all">全部导演</option>
              {allDirectors.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block font-label text-xs font-semibold text-foreground-600">影片类型</label>
            <select
              value={filters.genre}
              onChange={(e) => onChange({ genre: e.target.value })}
              className={selectClass}
              aria-label="按类型筛选"
            >
              <option value="all">全部类型</option>
              {allGenres.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block font-label text-xs font-semibold text-foreground-600">评分区间</label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-background-200 bg-background-50 p-1">
              {ratingOptions.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onChange({ rating: o.value })}
                  className={`flex-1 min-w-[64px] h-8 rounded-full px-2 font-label text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    filters.rating === o.value
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {o.value === 'all' ? '不限' : `${o.value}+`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="font-label text-xs font-semibold text-foreground-600">快速类型</span>
          <button
            type="button"
            onClick={() => onChange({ genre: 'all' })}
            className={`rounded-full px-3 py-1 font-label text-xs transition-colors cursor-pointer ${
              filters.genre === 'all'
                ? 'bg-accent-500 text-primary-950'
                : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
            }`}
          >
            全部
          </button>
          {allGenres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => onChange({ genre: g })}
              className={`rounded-full px-3 py-1 font-label text-xs transition-colors cursor-pointer ${
                filters.genre === g
                  ? 'bg-accent-500 text-primary-950'
                  : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
              }`}
            >
              {g}
            </button>
          ))}

          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-medium text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer"
            >
              <i className="ri-restart-line" aria-hidden="true" />
              重置筛选
            </button>
          )}
        </div>
      </div>
    </div>
  );
}